import React, { useState } from 'react';
import { Trip, TripMember } from '../../types/trip';
import { TravelEntry } from '../../types/travel';
import { useDesign } from '../../designs';
import { addTravelEntry, updateTravelEntry, deleteTravelEntry } from '../../services/travel';
import { Plane } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import { formatTime12h } from '../../components/ui/TimeSpinner';
import TravelTimeInput from '../../components/ui/TravelTimeInput';
import OrnamentalDivider from '../../components/ui/OrnamentalDivider';
import DesignButton from '../../components/ui/DesignButton';
import { getTripDays, formatDayOption } from '../../utils/dates';
import { getDisplayMembers } from '../../utils/members';

interface TravelTabProps {
  trip: Trip;
  tripId: string;
  isPlanner: boolean;
  canEdit: boolean;
  currentUid: string;
  claimedMemberUid: string | null;
}

/* ─── Travel Row ─── */
function TravelRow({ entry, member, canTap, onTap }: {
  entry: TravelEntry; member?: TripMember; canTap: boolean; onTap: () => void;
}) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const firstName = member?.displayName?.split(' ')[0] || 'Guest';

  // Flight info line: "2:30 PM · OAK · United 2287"
  // Landing time first (most important for coordination), airport, flight# last (least important — truncates first)
  const flightParts = [
    entry.flightTime ? formatTime12h(entry.flightTime) : null,
    entry.airport,
    entry.flightNumber,
  ].filter(Boolean);
  const flightLine = flightParts.length > 0 ? flightParts.join(' · ') : '';

  // On-site / leave-by time — single inline string ("5:00 PM")
  const siteTime = entry.arriveAtLodging ? formatTime12h(entry.arriveAtLodging) : '';

  return (
    <div onClick={canTap ? onTap : undefined} style={{
      display: 'grid', gridTemplateColumns: '80px 1fr auto',
      columnGap: '12px', alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      cursor: canTap ? 'pointer' : 'default',
    }}>
      {/* Name */}
      <span style={{
        fontFamily: "'Raleway', sans-serif", fontSize: fs.itemName, fontWeight: 700,
        color: d.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{firstName}</span>

      {/* Flight + transport */}
      <div style={{ minWidth: 0 }}>
        {flightLine && (
          <div style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
            color: d.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{flightLine}</div>
        )}
        {entry.transportToLodging && (
          <div style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
            fontStyle: 'italic', color: d.textSecondary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{entry.transportToLodging}</div>
        )}
        {!flightLine && !entry.transportToLodging && entry.notes && (
          <div style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyDetail, fontWeight: 300,
            fontStyle: 'italic', color: d.textSecondary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{entry.notes}</div>
        )}
      </div>

      {/* Time — single inline string */}
      {siteTime ? (
        <span style={{ fontFamily: displayFont, fontSize: fs.timestamp, fontWeight: 700, color: d.accent, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {siteTime}
        </span>
      ) : (
        <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300, color: d.textTertiary, textAlign: 'right' }}>—</span>
      )}
    </div>
  );
}

/* ─── Add/Edit Travel Modal ─── */
function TravelModal({ tripId, members, tripDays, existingEntry, isPlanner, claimedMemberUid, onClose }: {
  tripId: string; members: Record<string, TripMember>; tripDays: string[];
  existingEntry?: TravelEntry; isPlanner: boolean; claimedMemberUid: string | null; onClose: () => void;
}) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const isEditing = !!existingEntry;
  const memberList = getDisplayMembers(members);
  // Crew members can only add travel for themselves; planner can pick anyone
  const defaultMemberUid = existingEntry?.memberUid || (!isPlanner && claimedMemberUid ? claimedMemberUid : memberList[0]?.uid || '');
  const [memberUid, setMemberUid] = useState(defaultMemberUid);
  const isPersonLocked = !isPlanner && !!claimedMemberUid;
  const [direction, setDirection] = useState<'arrival' | 'departure'>(existingEntry?.direction || 'arrival');
  const [day, setDay] = useState(existingEntry?.day || tripDays[0] || '');
  const [airport, setAirport] = useState(existingEntry?.airport || '');
  const [flightNumber, setFlightNumber] = useState(existingEntry?.flightNumber || '');
  const [flightTime, setFlightTime] = useState(existingEntry?.flightTime || '');
  const [transportToLodging, setTransportToLodging] = useState(existingEntry?.transportToLodging || '');
  const [arriveAtLodging, setArriveAtLodging] = useState(existingEntry?.arriveAtLodging || '');
  const [notes, setNotes] = useState(existingEntry?.notes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px',
    fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
    color: d.textPrimary, backgroundColor: d.bgSurface,
    border: '1px solid rgba(200,146,58,0.10)', borderRadius: '2px',
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: "'Raleway', sans-serif",
    fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px',
    textTransform: 'uppercase', color: d.textSecondary, marginBottom: '6px',
  };

  const canSubmit = memberUid && day;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setSubmitError('');
    const data = {
      memberUid, direction, day,
      ...(airport ? { airport } : {}),
      ...(flightNumber ? { flightNumber } : {}),
      ...(flightTime ? { flightTime } : {}),
      ...(transportToLodging ? { transportToLodging } : {}),
      ...(arriveAtLodging ? { arriveAtLodging } : {}),
      ...(notes ? { notes } : {}),
      importedViaEmail: false,
    };
    try {
      if (isEditing && existingEntry) {
        await updateTravelEntry(tripId, existingEntry.entryId, data);
      } else {
        await addTravelEntry(tripId, data);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save travel:', err);
      setSubmitError('Failed to save — please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingEntry) return;
    const member = members[existingEntry.memberUid];
    if (!window.confirm(`Delete ${member?.displayName || 'this'} travel entry?`)) return;
    await deleteTravelEntry(tripId, existingEntry.entryId);
    onClose();
  };

  return (
    <Modal title={isEditing ? 'Edit Travel' : 'Add Travel'} onClose={onClose}>
      {/* Person + Direction side by side */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Person</label>
          <select value={memberUid} onChange={(e) => setMemberUid(e.target.value)}
            disabled={isPersonLocked}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={{ ...inputStyle, opacity: isPersonLocked ? 0.6 : 1 }}>
            {memberList.map((m) => <option key={m.uid} value={m.uid}>{m.displayName.split(' ')[0]}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Direction</label>
          <select value={direction} onChange={(e) => setDirection(e.target.value as 'arrival' | 'departure')}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={inputStyle}>
            <option value="arrival">Arrival</option>
            <option value="departure">Departure</option>
          </select>
        </div>
      </div>
      {/* Day */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Day</label>
        <select value={day} onChange={(e) => setDay(e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={inputStyle}>
          {tripDays.map((d) => <option key={d} value={d}>{formatDayOption(d)}</option>)}
        </select>
      </div>
      {/* Airport + Flight # side by side */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Airport</label>
          <input type="text" value={airport} onChange={(e) => setAirport(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={inputStyle} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Flight #</label>
          <input type="text" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)}
            onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
            style={inputStyle} />
        </div>
      </div>
      {/* Flight Time + On Site / Leave By side by side */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '4px' }}>
        <TravelTimeInput label="Flight Time" value={flightTime} onChange={setFlightTime} />
        <TravelTimeInput label={direction === 'departure' ? 'Leave Site' : 'Arrive on Site'} value={arriveAtLodging} onChange={setArriveAtLodging} />
      </div>
      {/* Time validation warning — soft, does not block save */}
      {(() => {
        if (!flightTime || !arriveAtLodging) return null;
        const isArrivalMismatch = direction === 'arrival' && arriveAtLodging < flightTime;
        const isDepartureMismatch = direction === 'departure' && arriveAtLodging > flightTime;
        if (!isArrivalMismatch && !isDepartureMismatch) return null;
        const msg = isArrivalMismatch
          ? 'On-site time is before arrival time'
          : 'Leave-by time is after departure time';
        return (
          <p style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
            color: d.accent, margin: '0 0 12px',
          }}>{msg}</p>
        );
      })()}
      <div style={{ marginBottom: '12px' }} />
      {/* Transport */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Transport</label>
        <input type="text" value={transportToLodging} onChange={(e) => setTransportToLodging(e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={inputStyle} />
      </div>
      {/* Notes */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
      </div>
      {submitError && (
        <p style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.error, marginBottom: '8px', textAlign: 'center' }}>{submitError}</p>
      )}
      <DesignButton tier={2} fullWidth onClick={handleSubmit} disabled={isSubmitting || !canSubmit}>
        {isSubmitting ? 'Saving...' : isEditing ? 'Save' : 'Add Travel'}
      </DesignButton>
      {isEditing && (
        <button type="button" onClick={handleDelete} style={{
          display: 'block', width: '100%', marginTop: '12px', padding: '8px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.error, textAlign: 'center',
        }}>Delete this entry</button>
      )}
    </Modal>
  );
}

/* ─── Main TravelTab ─── */
export default function TravelTab({ trip, tripId, isPlanner, canEdit, currentUid, claimedMemberUid }: TravelTabProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState<TravelEntry | null>(null);
  const members = trip.members || {};
  const meta = trip.meta;
  const tripDays = getTripDays(meta.startDate, meta.endDate);
  const entries = trip.travel ? Object.values(trip.travel) : [];

  const arrivals = entries.filter((e) => e.direction === 'arrival').sort((a, b) => {
    if (a.day !== b.day) return a.day.localeCompare(b.day);
    return (a.arriveAtLodging || '').localeCompare(b.arriveAtLodging || '');
  });
  const departures = entries.filter((e) => e.direction === 'departure').sort((a, b) => {
    if (a.day !== b.day) return a.day.localeCompare(b.day);
    return (a.arriveAtLodging || '').localeCompare(b.arriveAtLodging || '');
  });

  const groupByDay = (list: TravelEntry[]) => {
    const grouped: Record<string, TravelEntry[]> = {};
    list.forEach((entry) => {
      if (!grouped[entry.day]) grouped[entry.day] = [];
      grouped[entry.day].push(entry);
    });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  const arrivalGroups = groupByDay(arrivals);
  const departureGroups = groupByDay(departures);

  // Missing travel detection — only check display members
  const allMembers = getDisplayMembers(members);
  const arrivalUids = new Set(arrivals.map((e) => e.memberUid));
  const departureUids = new Set(departures.map((e) => e.memberUid));
  const missing: string[] = [];
  allMembers.forEach((m) => {
    const hasArr = arrivalUids.has(m.uid);
    const hasDep = departureUids.has(m.uid);
    const firstName = m.displayName?.split(' ')[0] || 'Guest';
    if (!hasArr && !hasDep) missing.push(firstName);
    else if (!hasArr) missing.push(`${firstName} (arrival)`);
    else if (!hasDep) missing.push(`${firstName} (departure)`);
  });

  const formatDaySub = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
    return `${weekday} · ${monthDay}`;
  };

  const canEditEntry = (entry: TravelEntry) => {
    if (isPlanner) return true;
    if (!claimedMemberUid) return false;
    return entry.memberUid === claimedMemberUid;
  };

  const handleRowTap = (entry: TravelEntry) => {
    setEditEntry(entry);
    setShowModal(true);
  };

  const renderSection = (
    label: string,
    columnLabel: string,
    groups: [string, TravelEntry[]][],
  ) => {
    if (groups.length === 0) return null;
    const showDaySubs = groups.length > 1;
    return (
      <div style={{ marginBottom: '32px' }}>
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Plane size={12} strokeWidth={1.5} color={d.accent} />
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
            letterSpacing: '4px', textTransform: 'uppercase', color: d.accent, flexShrink: 0,
          }}>{label}</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(200,146,58,0.20)' }} />
        </div>

        {groups.map(([day, dayEntries], groupIdx) => (
          <div key={day}>
            {/* Day sub-header */}
            {(showDaySubs || groupIdx === 0) && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                marginBottom: '6px', marginTop: groupIdx > 0 ? '16px' : '0',
              }}>
                <span style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.dayHeader, fontWeight: 600,
                  letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(147,142,164,0.60)',
                }}>{formatDaySub(day)}</span>
                {groupIdx === 0 && (
                  <span style={{
                    fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
                    letterSpacing: '3.5px', textTransform: 'uppercase', color: d.textSecondary,
                  }}>{columnLabel}</span>
                )}
              </div>
            )}
            {dayEntries.map((entry) => (
              <TravelRow key={entry.entryId} entry={entry} member={members[entry.memberUid]} canTap={canEditEntry(entry)} onTap={() => handleRowTap(entry)} />
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: '0 26px' }}>
      {/* Add Travel CTA — visible to planner and claimed crew only */}
      {canEdit && (
        <div style={{ marginBottom: '24px' }}>
          <DesignButton tier={2} fullWidth onClick={() => { setEditEntry(null); setShowModal(true); }}>
            Add Travel
          </DesignButton>
        </div>
      )}

      {/* Arrivals */}
      {renderSection('Arrivals', 'On Site', arrivalGroups)}

      {/* Departures */}
      {renderSection('Departures', 'Leave By', departureGroups)}

      {/* Empty state */}
      {entries.length === 0 && (
        <div style={{ padding: '48px 16px 24px', textAlign: 'center' }}>
          <p style={{
            fontFamily: displayFont, fontSize: fs.bodyLarge, fontWeight: 600,
            fontStyle: 'italic', color: d.textSecondary,
            margin: 0, lineHeight: 1.4,
          }}>
            No travel added yet.<br />Add flights, drives, or trains to keep everyone coordinated.
          </p>
        </div>
      )}

      {/* Missing travel */}
      {missing.length > 0 && entries.length > 0 && (
        <div style={{
          borderTop: `1px solid rgba(196,164,74,0.15)`,
          paddingTop: '10px', marginTop: '8px', marginBottom: '8px',
        }}>
          <span style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
            color: d.warning,
          }}>Missing: {missing.join(', ')}</span>
        </div>
      )}

      {/* Design closer */}
      <div style={{ padding: '28px 0' }}>
        <OrnamentalDivider style={{ margin: '0 0 12px' }} />
        <div style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.wordmark, fontWeight: 800,
          letterSpacing: '9px', textTransform: 'uppercase',
          color: 'rgba(200,146,58,0.20)', textAlign: 'center', marginBottom: '4px',
        }}>KREWE</div>
        <div style={{
          fontFamily: displayFont, fontSize: fs.detail, fontWeight: 400,
          fontStyle: 'italic', color: 'rgba(200,146,58,0.25)', textAlign: 'center',
        }}>{meta.destination}</div>
      </div>

      {showModal && (
        <TravelModal tripId={tripId} members={members} tripDays={tripDays} existingEntry={editEntry || undefined} isPlanner={isPlanner} claimedMemberUid={claimedMemberUid} onClose={() => { setShowModal(false); setEditEntry(null); }} />
      )}
    </div>
  );
}
